/** Browser-side geo lookup (visitor IP / location, not the app server). */
export interface ClientGeo {
  ip?: string;
  country?: string;
  city?: string;
  source?: 'ipapi' | 'cloudflare' | 'timezone';
}

const TIMEZONE_COUNTRY: Record<string, string> = {
  'Asia/Karachi': 'PK',
  'Europe/London': 'GB',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'Europe/Berlin': 'DE',
  'Europe/Paris': 'FR',
  'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Dublin': 'IE',
  'Europe/Vienna': 'AT',
  'Europe/Lisbon': 'PT',
  'Europe/Athens': 'GR',
  'Europe/Helsinki': 'FI',
  'Europe/Warsaw': 'PL',
  'Europe/Stockholm': 'SE',
  'Europe/Copenhagen': 'DK',
  'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU',
  'Europe/Bucharest': 'RO',
  'Europe/Sofia': 'BG',
  'Asia/Riyadh': 'SA',
  'Asia/Dubai': 'AE',
  'Asia/Qatar': 'QA',
  'Asia/Bahrain': 'BH',
  'Asia/Muscat': 'OM',
  'Asia/Kuwait': 'KW',
};

function countryFromTimezone(timezone?: string): string | undefined {
  if (!timezone) return undefined;
  return TIMEZONE_COUNTRY[timezone];
}

async function fetchFromIpapi(): Promise<ClientGeo> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return {};
    const data = (await res.json()) as {
      ip?: string;
      country_code?: string;
      city?: string;
      error?: boolean;
    };
    if (data.error || !data.country_code) return {};
    return {
      ip: data.ip,
      country: data.country_code.toUpperCase(),
      city: data.city,
      source: 'ipapi',
    };
  } catch {
    return {};
  } finally {
    clearTimeout(timer);
  }
}

async function fetchFromCloudflareTrace(): Promise<ClientGeo> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
      signal: controller.signal,
    });
    if (!res.ok) return {};
    const text = await res.text();
    const ip = text.match(/^ip=(.+)$/m)?.[1]?.trim();
    const loc = text.match(/^loc=(.+)$/m)?.[1]?.trim()?.toUpperCase();
    if (!loc || loc.length !== 2) return {};
    return { ip, country: loc, source: 'cloudflare' };
  } catch {
    return {};
  } finally {
    clearTimeout(timer);
  }
}

/** Resolve visitor country/IP using browser APIs only (never the React/API server). */
export async function fetchClientGeo(): Promise<ClientGeo> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const ipapi = await fetchFromIpapi();
  if (ipapi.country) return ipapi;

  const cf = await fetchFromCloudflareTrace();
  if (cf.country) return cf;

  const tzCountry = countryFromTimezone(timezone);
  if (tzCountry) {
    return { country: tzCountry, source: 'timezone' };
  }

  return {};
}

export function inferCountryFromTimezone(timezone?: string): string | undefined {
  return countryFromTimezone(timezone);
}
